import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Eye, EyeOff, CheckCircle, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [state, setState] = useState("validating"); // validating | valid | invalid | success
  const [maskedPhone, setMaskedPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    axios.get(`${API}/auth/reset-password/validate?token=${encodeURIComponent(token)}`)
      .then(r => {
        setState("valid");
        setMaskedPhone(r.data.masked_phone || "");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords do not match");

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      // Auto-login
      localStorage.setItem("memberToken", res.data.token);
      localStorage.setItem("memberData", JSON.stringify(res.data.member));
      setState("success");
      setTimeout(() => {
        const isAdmin = res.data.member?.is_admin;
        if (isAdmin) {
          localStorage.setItem("adminToken", res.data.token);
          navigate("/admin");
        } else {
          navigate("/dashboard");
        }
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed. Please request a new link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="/nbc_logo.jpg"
            alt="NBC"
            className="h-16 mx-auto mb-6"
          />
          <h1 className="text-3xl font-bold uppercase">Create New Password</h1>
        </div>

        <div className="bg-[#0A0A0A] border border-white/10 p-8">

          {/* Validating */}
          {state === "validating" && (
            <div className="text-center py-8 text-white/40">Validating your link…</div>
          )}

          {/* Invalid token */}
          {state === "invalid" && (
            <div className="text-center space-y-4" data-testid="invalid-token-msg">
              <XCircle className="w-14 h-14 text-red-400 mx-auto" />
              <p className="font-bold text-lg text-red-400">Invalid or Expired Link</p>
              <p className="text-white/60 text-sm">This reset link is no longer valid. Links expire after 15 minutes and can only be used once.</p>
              <Link to="/login">
                <Button className="w-full bg-[#FFB612] text-black hover:bg-[#E5A00D] font-bold mt-2">
                  Request a New Link
                </Button>
              </Link>
            </div>
          )}

          {/* Valid — show password form */}
          {state === "valid" && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {maskedPhone && (
                <p className="text-sm text-white/50 text-center">
                  Resetting password for <span className="text-white font-mono">{maskedPhone}</span>
                </p>
              )}

              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="New Password (min 6 chars)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-[#050505] border-white/20 h-12 pl-12 pr-12 text-white placeholder:text-white/40"
                  autoFocus
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/70"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirm New Password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className={`bg-[#050505] h-12 pl-12 text-white placeholder:text-white/40 ${
                    confirm && confirm !== password ? "border-red-500/60" : "border-white/20"
                  }`}
                  data-testid="confirm-password-input"
                />
              </div>

              {confirm && confirm !== password && (
                <p className="text-xs text-red-400">Passwords do not match</p>
              )}

              <Button
                type="submit"
                disabled={loading || !password || !confirm}
                className="w-full bg-[#FFB612] text-black hover:bg-[#E5A00D] font-bold uppercase tracking-wide h-12"
                data-testid="reset-submit-btn"
              >
                {loading ? "Saving…" : "Set New Password"}
              </Button>
            </form>
          )}

          {/* Success */}
          {state === "success" && (
            <div className="text-center space-y-4" data-testid="reset-success-msg">
              <CheckCircle className="w-14 h-14 text-green-400 mx-auto" />
              <p className="font-bold text-lg text-green-400">Password Updated!</p>
              <p className="text-white/60 text-sm">You're being logged in automatically…</p>
            </div>
          )}
        </div>

        <p className="text-center mt-6 text-white/40 text-sm">
          <Link to="/login" className="hover:text-[#FFB612]">← Back to Login</Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
